import React from 'react'
import ReactDOM from 'react-dom'
import { formatEur, formatRange, humanizeCohortName } from './networkUtils'

/**
 * Per-opportunity SVG visualizations, ported from the supply-chain demo and
 * extended with a cross-site price-gap chart. Each component reads opp.vizData.
 */

const TEXT_SECONDARY = '#475569'
const TEXT_TERTIARY = '#94a3b8'

function Insight({ children }) {
  return (
    <div className="mt-2 text-[11px] text-gray-600 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 leading-relaxed">
      {children}
    </div>
  )
}

function ChartFrame({ children, minWidth = 500 }) {
  return (
    <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
      <div style={{ minWidth }}>{children}</div>
    </div>
  )
}

function NegoViz({ opp, onPartClick }) {
  const allItems = [...opp.vizData.items].sort((a, b) => b.pp - a.pp)
  const goodItems = opp.vizData.goodItems || []
  const [expanded, setExpanded] = React.useState(false)
  const [hovered, setHovered] = React.useState(null)
  const [tipStyle, setTipStyle] = React.useState({})
  const hideTimer = React.useRef(null)
  const items = expanded ? allItems : allItems.slice(0, 10)
  const maxPP = Math.max(2.5, ...allItems.map(i => i.pp))
  const ROW_H = 32
  const goodSection = goodItems.length > 0 ? goodItems.slice(0, expanded ? 8 : 4) : []
  const GOOD_TOP_OFFSET = items.length * ROW_H + 60
  const W = 800, H = Math.max(220, items.length * ROW_H + (goodSection.length > 0 ? goodSection.length * ROW_H + 80 : 0) + 140)
  const LEFT = 240, RIGHT = 130, TOP = 80
  const chartW = W - LEFT - RIGHT
  const x = pp => LEFT + (pp / maxPP) * chartW
  const benchX = x(1.0), threshX = x(1.3)

  // Compute median PP for reference
  const sortedPPs = allItems.map(i => i.pp).sort((a, b) => a - b)
  const medianPP = sortedPPs.length > 0
    ? (sortedPPs.length % 2 === 0
      ? (sortedPPs[sortedPPs.length / 2 - 1] + sortedPPs[sortedPPs.length / 2]) / 2
      : sortedPPs[Math.floor(sortedPPs.length / 2)])
    : 1.0
  const medianX = x(medianPP)

  // Build a lookup from pn -> full part data (for click handler and tooltip)
  const partMap = React.useMemo(() => {
    const m = {}
    ;(opp.parts || []).forEach(p => { m[p.pn] = p })
    return m
  }, [opp.parts])

  const handlePartClick = (pn) => {
    if (!onPartClick) return
    const part = partMap[pn]
    if (part) { onPartClick({ fileId: part.file_id || null, filename: part.filename }); return }
    const gi = goodItems.find(g => g.pn === pn)
    if (gi) onPartClick({ fileId: gi.file_id || null, filename: gi.filename })
  }

  const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null } }
  const scheduleHide = () => { cancelHide(); hideTimer.current = setTimeout(() => setHovered(null), 150) }

  const handleBarEnter = (e, idx) => {
    cancelHide()
    const rect = e.currentTarget.getBoundingClientRect()
    const tipW = 400
    const spaceBelow = window.innerHeight - rect.bottom
    const above = spaceBelow < 250
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - tipW / 2, window.innerWidth - tipW - 12))
    if (above) {
      setTipStyle({ position: 'fixed', bottom: window.innerHeight - rect.top + 6, left, maxWidth: tipW, zIndex: 9999 })
    } else {
      setTipStyle({ position: 'fixed', top: rect.bottom + 6, left, maxWidth: tipW, zIndex: 9999 })
    }
    setHovered(idx)
  }

  const handleBarLeave = () => { scheduleHide() }
  const handleTipEnter = () => { cancelHide() }
  const handleTipLeave = () => { scheduleHide() }

  // Render tooltip — handles both overpriced (positive idx) and good items (negative idx offset)
  const renderTooltip = () => {
    if (hovered == null) return null
    const isGood = hovered >= 1000
    const item = isGood ? goodSection[hovered - 1000] : items[hovered]
    if (!item) return null
    const fullPart = partMap[item.pn]
    // For good items not in partMap, use cohort data stored on the item itself
    const sup = fullPart?.suppliers?.find(s => s.name === opp.vizData.supplierName)
      || (isGood && item.cohort_entries?.length > 0 ? { cohort_entries: item.cohort_entries, cohort_name: item.cohort_name, cohort_size: item.cohort_size } : null)
    const overspend = item.pp > 1 ? item.spend * (1 - 1 / item.pp) : 0

    return ReactDOM.createPortal(
      <div
        onMouseEnter={handleTipEnter}
        onMouseLeave={handleTipLeave}
        style={tipStyle}
        className="bg-white text-gray-700 rounded-lg shadow-lg border border-gray-200 px-3 py-2.5"
      >
        <div className="text-[10px] text-gray-500 mb-1 truncate">
          <span className="font-mono font-medium text-gray-700">{item.pn}</span>
          {item.desc && <span className="ml-1.5">{item.desc}</span>}
        </div>
        <div className="font-semibold text-[11px] text-gray-900 truncate mb-1">{opp.vizData.supplierName}</div>
        <table className="text-[10px]">
          <tbody>
            <tr><td className="text-gray-500 pr-2 py-0.5">Unit Cost</td><td className="font-mono text-gray-900">{'\u20AC'}{item.uc.toFixed(2)}</td></tr>
            {item.bm > 0 && <tr><td className="text-gray-500 pr-2 py-0.5">Benchmark</td><td className="font-mono text-gray-900">{'\u20AC'}{item.bm.toFixed(2)}</td></tr>}
            <tr><td className="text-gray-500 pr-2 py-0.5">Price Position</td><td className={`font-semibold ${item.pp > 1.5 ? 'text-red-600' : item.pp > 1.3 ? 'text-amber-600' : 'text-green-700'}`}>{item.pp.toFixed(2)}x {item.pp > 1 ? `(${Math.round((item.pp - 1) * 100)}% above)` : item.pp < 1 ? `(${Math.round((1 - item.pp) * 100)}% below)` : '(at benchmark)'}</td></tr>
            <tr><td className="text-gray-500 pr-2 py-0.5">Annual Spend</td><td className="font-mono text-gray-900">{'\u20AC'}{Math.round(item.spend).toLocaleString()}</td></tr>
            {item.eau > 0 && <tr><td className="text-gray-500 pr-2 py-0.5">EAU</td><td className="font-mono text-gray-900">{Math.round(item.eau).toLocaleString()}</td></tr>}
            {overspend > 0 && <tr><td className="text-gray-500 pr-2 py-0.5">Overspend</td><td className="font-mono font-semibold text-red-600">{'\u20AC'}{Math.round(overspend).toLocaleString()}</td></tr>}
            {isGood && <tr><td className="text-gray-500 pr-2 py-0.5">Status</td><td className="font-semibold text-green-700">{'\u2713'} Competitive</td></tr>}
            {!isGood && item.bm > 0 && <tr><td className="text-gray-500 pr-2 py-0.5">Target (1.1x BM)</td><td className="font-mono text-green-700">{'\u20AC'}{(item.bm * 1.1).toFixed(2)}</td></tr>}
          </tbody>
        </table>
        {/* Cohort context */}
        {sup?.cohort_entries?.length > 0 && (
          <div className="mt-2 pt-1.5 border-t border-gray-100">
            <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              {humanizeCohortName(sup.cohort_name)} ({sup.cohort_size || sup.cohort_entries.length})
            </div>
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left font-medium pb-0.5 pr-2">Part</th>
                  <th className="text-left font-medium pb-0.5 pr-2">Supplier</th>
                  <th className="text-right font-medium pb-0.5 pr-2">UC</th>
                  <th className="text-right font-medium pb-0.5">EAU</th>
                </tr>
              </thead>
              <tbody>
                {sup.cohort_entries.sort((a, b) => b.unit_cost - a.unit_cost).slice(0, 6).map((e, i) => (
                  <tr key={i} className={e.supplier === opp.vizData.supplierName ? 'text-gray-900 font-medium' : 'text-gray-600'}>
                    <td className="pr-2 py-0.5 truncate max-w-[100px] font-mono">{e.pn?.slice(0, 12) || '\u2014'}</td>
                    <td className="pr-2 py-0.5 truncate max-w-[100px]">{e.supplier?.slice(0, 14)}</td>
                    <td className="text-right pr-2 py-0.5 font-mono whitespace-nowrap">{'\u20AC'}{e.unit_cost.toFixed(2)}</td>
                    <td className="text-right py-0.5 font-mono whitespace-nowrap">{e.eau > 0 ? Math.round(e.eau).toLocaleString() : '\u2014'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>,
      document.body
    )
  }

  return (
    <>
      <ChartFrame>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
          <text x={W - RIGHT} y={20} textAnchor="end" fontSize="12" fontWeight="700" fill="#059669">Potential savings: {formatRange(opp.estSavings)}</text>
          {/* Supplier label */}
          <text x={LEFT} y={20} fontSize="11" fontWeight="600" fill={TEXT_SECONDARY}>Supplier: {opp.vizData.supplierName}</text>
          <text x={LEFT} y={35} fontSize="9" fill={TEXT_TERTIARY}>{allItems.length} overpriced{goodItems.length > 0 ? ` + ${goodItems.length} competitive` : ''}{` \u00B7 avg ${(opp.avgPP || 0).toFixed(2)}x`}{` \u00B7 ${formatEur(opp.spendExposed)} total spend`}</text>
          {/* Header */}
          <text x={LEFT} y={TOP - 12} fontSize="10" fill="#dc2626" fontWeight="600">{'\u26A0'} Above Benchmark</text>
          <text x={LEFT - 8} y={TOP - 12} textAnchor="end" fontSize="10" fill={TEXT_TERTIARY}>Part Number</text>
          {/* Overspend zone background */}
          <rect x={threshX} y={TOP - 4} width={W - RIGHT - threshX} height={items.length * ROW_H + 8} fill="rgba(239,68,68,0.04)" rx="4" />
          {/* Benchmark reference line (1.0x) */}
          <line x1={benchX} y1={TOP - 4} x2={benchX} y2={TOP + items.length * ROW_H + (goodSection.length > 0 ? goodSection.length * ROW_H + 60 : 0)} stroke="#10b981" strokeWidth="1.5" strokeDasharray="4 3" />
          {/* Threshold line (1.3x) */}
          <line x1={threshX} y1={TOP - 4} x2={threshX} y2={TOP + items.length * ROW_H + (goodSection.length > 0 ? goodSection.length * ROW_H + 60 : 0)} stroke="#ef4444" strokeWidth="1" strokeDasharray="3 4" opacity="0.7" />
          {/* Median reference line */}
          {medianPP > 1.0 && (
            <line x1={medianX} y1={TOP - 4} x2={medianX} y2={TOP + items.length * ROW_H} stroke="#6366f1" strokeWidth="1.2" strokeDasharray="3 4" opacity="0.7" />
          )}
          {/* Per-part bars (overpriced) */}
          {items.map((item, i) => {
            const cy = TOP + i * ROW_H
            const barW = Math.max(0, (item.pp / maxPP) * chartW)
            const color = item.pp > 1.5 ? '#ef4444' : item.pp > 1.3 ? '#f59e0b' : '#10b981'
            const partLabel = item.desc ? `${item.pn} (${item.desc})` : (item.pn || '')
            return (
              <g key={i}>
                <text
                  x={LEFT - 8} y={cy + 16} textAnchor="end" fontSize="10"
                  fill={onPartClick ? '#1e5045' : TEXT_SECONDARY}
                  style={onPartClick ? { cursor: 'pointer' } : undefined}
                  className={onPartClick ? 'hover:underline' : ''}
                  onClick={() => handlePartClick(item.pn)}
                >{partLabel.slice(0, 30)}</text>
                <rect
                  x={LEFT} y={cy + 2} width={barW} height="20" rx="3" fill={color} opacity="0.8"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => handleBarEnter(e, i)}
                  onMouseLeave={handleBarLeave}
                />
                <text
                  x={LEFT + barW + 5} y={cy + 16} fontSize="10" fill={TEXT_TERTIARY}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => handleBarEnter(e, i)}
                  onMouseLeave={handleBarLeave}
                >{item.pp.toFixed(2)}x  {formatEur(item.spend * (1 - 1 / item.pp))}</text>
              </g>
            )
          })}
          {!expanded && allItems.length > 10 && <text x={LEFT} y={TOP + items.length * ROW_H + 14} fontSize="9" fill={TEXT_TERTIARY}>+ {allItems.length - 10} more parts</text>}
          {/* Good items section (competitive pricing from same supplier) */}
          {goodSection.length > 0 && (
            <g>
              {/* Separator */}
              <line x1={LEFT - 8} y1={TOP + GOOD_TOP_OFFSET - 30} x2={W - RIGHT} y2={TOP + GOOD_TOP_OFFSET - 30} stroke="#e2e8f0" strokeWidth="1" />
              <text x={LEFT} y={TOP + GOOD_TOP_OFFSET - 14} fontSize="10" fill="#059669" fontWeight="600">{'\u2713'} Competitive (at/below benchmark)</text>
              {/* Good item bars */}
              {goodSection.map((item, i) => {
                const cy = TOP + GOOD_TOP_OFFSET + i * ROW_H
                const barW = Math.max(0, (item.pp / maxPP) * chartW)
                const partLabel = item.desc ? `${item.pn} (${item.desc})` : (item.pn || '')
                return (
                  <g key={'g' + i}>
                    <text
                      x={LEFT - 8} y={cy + 16} textAnchor="end" fontSize="10"
                      fill={onPartClick ? '#1e5045' : TEXT_TERTIARY}
                      style={onPartClick ? { cursor: 'pointer' } : undefined}
                      className={onPartClick ? 'hover:underline' : ''}
                      onClick={() => handlePartClick(item.pn)}
                    >{partLabel.slice(0, 30)}</text>
                    <rect
                      x={LEFT} y={cy + 2} width={barW} height="20" rx="3" fill="#10b981" opacity="0.5"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => handleBarEnter(e, 1000 + i)}
                      onMouseLeave={handleBarLeave}
                    />
                    <text
                      x={LEFT + barW + 5} y={cy + 16} fontSize="10" fill="#059669"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => handleBarEnter(e, 1000 + i)}
                      onMouseLeave={handleBarLeave}
                    >{item.pp.toFixed(2)}x  {formatEur(item.spend)}{item.eau > 0 ? `  EAU ${Math.round(item.eau).toLocaleString()}` : ''}</text>
                  </g>
                )
              })}
              {goodItems.length > goodSection.length && <text x={LEFT} y={TOP + GOOD_TOP_OFFSET + goodSection.length * ROW_H + 14} fontSize="9" fill={TEXT_TERTIARY}>+ {goodItems.length - goodSection.length} more competitive parts</text>}
            </g>
          )}
          {/* Reference line labels at bottom */}
          <text x={benchX + 4} y={H - 20} fontSize="9" fill="#059669">Benchmark (1.0x)</text>
          <text x={threshX + 4} y={H - 8} fontSize="9" fill="#ef4444">Threshold (1.3x)</text>
          {medianPP > 1.0 && <text x={medianX + 4} y={H - 32} fontSize="9" fill="#6366f1">Median: {medianPP.toFixed(2)}x</text>}
        </svg>
      </ChartFrame>
      {renderTooltip()}
      {allItems.length > 10 && (
        <button onClick={() => setExpanded(!expanded)} className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium mt-1 mb-1 cursor-pointer">
          {expanded ? '\u25B2 Show fewer' : `\u25BC Show all ${allItems.length} parts`}
        </button>
      )}
      <Insight>
        {goodItems.length > 0
          ? `${allItems.length} parts are priced above benchmark (red/amber zone), while ${goodItems.length} parts from the same supplier are competitive (green). Use the competitive parts as proof: "${opp.vizData.supplierName} can price competitively \u2014 we need the same discipline on these ${allItems.length} outliers."`
          : `Each bar shows how much a part costs relative to its cohort benchmark. Parts beyond 1.3x (red zone) are priced above expected \u2014 the gap is your negotiation potential.`}
        {' '}Hover for details, click part labels to open the engineering record.
      </Insight>
    </>
  )
}

const RING_COLORS = ['#ef4444', '#f59e0b', '#f97316', '#8b5cf6', '#06b6d4', '#10b981', '#ec4899', '#6366f1']

function SoleSourceViz({ opp }) {
  const d = opp.vizData
  const parts = d.topParts
  const total = parts.reduce((s, p) => s + p.spend, 0)
  const rest = d.totalSpend - total
  const W = 700, H = Math.max(220, parts.length * 24 + 60), CX = 130, CY = H / 2, R = 70
  const C = 2 * Math.PI * R
  let cum = 0
  const segs = parts.map((p, i) => {
    const pct = p.spend / d.totalSpend
    const seg = <circle key={i} cx={CX} cy={CY} r={R} fill="none" stroke={RING_COLORS[i % RING_COLORS.length]} strokeWidth="24" strokeDasharray={`${C * pct} ${C - C * pct}`} strokeDashoffset={-C * cum} />
    cum += pct
    return seg
  })
  let restSeg = null
  if (rest > 0) { restSeg = <circle cx={CX} cy={CY} r={R} fill="none" stroke="#d1d5db" strokeWidth="24" strokeDasharray={`${C * (rest / d.totalSpend)} ${C - C * (rest / d.totalSpend)}`} strokeDashoffset={-C * cum} /> }
  return (
    <>
      <ChartFrame>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
          {segs}{restSeg}
          <text x={CX} y={CY - 8} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1a1a2e">100%</text>
          <text x={CX} y={CY + 8} textAnchor="middle" fontSize="9" fill={TEXT_TERTIARY}>single source</text>
          <text x={W - 10} y={18} textAnchor="end" fontSize="12" fontWeight="700" fill="#059669">Potential savings: {formatRange(opp.estSavings)}</text>
          {parts.slice(0, 8).map((p, i) => {
            const ly = 30 + i * 22
            const label = p.desc ? `${p.pn} - ${p.desc}` : p.pn
            return (
              <g key={i}>
                <rect x="230" y={ly} width="12" height="12" rx="2" fill={RING_COLORS[i % RING_COLORS.length]} />
                <text x="248" y={ly + 11} fontSize="11" fill={TEXT_SECONDARY}>{label.slice(0, 40)} ({formatEur(p.spend)})</text>
              </g>
            )
          })}
          {d.partCount > 8 && <text x="248" y={30 + 8 * 22 + 11} fontSize="11" fill={TEXT_TERTIARY}>+ {d.partCount - 8} more parts</text>}
        </svg>
      </ChartFrame>
      <Insight>⚠️ All {formatEur(d.totalSpend)} concentrated at <strong>{d.supplierName}</strong> with no backup. Dual-sourcing reduces risk and enables competitive pricing.</Insight>
    </>
  )
}

function PartFamilyViz({ opp, onPartClick }) {
  const d = opp.vizData
  const allParts = d.partPrices || []
  const [expanded, setExpanded] = React.useState(false)
  const [hovered, setHovered] = React.useState(null) // index of hovered bar
  const [tipStyle, setTipStyle] = React.useState({})
  const hideTimer = React.useRef(null)
  const parts = expanded ? allParts : allParts.slice(0, 10)
  const suppliers = d.suppliers || []
  const maxUC = allParts.length > 0 ? Math.max(...allParts.map(p => p.uc)) : 1
  const ROW_H = 32
  const W = 800, H = Math.max(220, parts.length * ROW_H + 160)
  const LEFT = 240, RIGHT = 130, TOP = 80
  const chartW = W - LEFT - RIGHT
  // Color palette for suppliers
  const SUP_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']
  const supColorMap = {}
  suppliers.forEach((s, i) => { supColorMap[s.name] = SUP_COLORS[i % SUP_COLORS.length] })

  const bestUC = d.best?.uc || 0
  const bestX = bestUC > 0 ? LEFT + (bestUC / maxUC) * chartW : 0

  // Compute median UC for the cohort reference line
  const sortedUCs = allParts.map(p => p.uc).sort((a, b) => a - b)
  const medianUC = sortedUCs.length > 0
    ? (sortedUCs.length % 2 === 0
      ? (sortedUCs[sortedUCs.length / 2 - 1] + sortedUCs[sortedUCs.length / 2]) / 2
      : sortedUCs[Math.floor(sortedUCs.length / 2)])
    : 0
  const medianX = medianUC > 0 ? LEFT + (medianUC / maxUC) * chartW : 0

  // Build a lookup from pn -> full part data (for click handler and tooltip)
  const partMap = React.useMemo(() => {
    const m = {}
    ;(opp.parts || []).forEach(p => { m[p.pn] = p })
    return m
  }, [opp.parts])

  const handlePartClick = (pn) => {
    if (!onPartClick) return
    const part = partMap[pn]
    if (part) onPartClick({ fileId: part.file_id || null, filename: part.filename })
  }

  const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null } }
  const scheduleHide = () => { cancelHide(); hideTimer.current = setTimeout(() => setHovered(null), 150) }

  const handleBarEnter = (e, idx) => {
    cancelHide()
    const rect = e.currentTarget.getBoundingClientRect()
    const tipW = 420
    const spaceBelow = window.innerHeight - rect.bottom
    const above = spaceBelow < 250
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - tipW / 2, window.innerWidth - tipW - 12))
    if (above) {
      setTipStyle({ position: 'fixed', bottom: window.innerHeight - rect.top + 6, left, maxWidth: tipW, zIndex: 9999 })
    } else {
      setTipStyle({ position: 'fixed', top: rect.bottom + 6, left, maxWidth: tipW, zIndex: 9999 })
    }
    setHovered(idx)
  }

  const handleBarLeave = () => { scheduleHide() }
  const handleTipEnter = () => { cancelHide() }
  const handleTipLeave = () => { scheduleHide() }

  // Render tooltip content for hovered bar
  const renderTooltip = () => {
    if (hovered == null) return null
    const p = parts[hovered]
    if (!p) return null
    const fullPart = partMap[p.pn]
    // Find the matching supplier object from the part's suppliers array
    const sup = fullPart?.suppliers?.find(s => s.name === p.supplier) || null
    const pp = sup?.price_position
    const hasPP = pp != null && sup?.benchmark

    return ReactDOM.createPortal(
      <div
        onMouseEnter={handleTipEnter}
        onMouseLeave={handleTipLeave}
        style={tipStyle}
        className="bg-white text-gray-700 rounded-lg shadow-lg border border-gray-200 px-3 py-2.5"
      >
        <div className="text-[10px] text-gray-500 mb-1 truncate">
          <span className="font-mono font-medium text-gray-700">{p.pn}</span>
          {p.desc && <span className="ml-1.5">{p.desc}</span>}
        </div>
        <div className="font-semibold text-[11px] text-gray-900 truncate mb-1">{p.supplier}</div>
        <table className="text-[10px]">
          <tbody>
            <tr><td className="text-gray-500 pr-2 py-0.5">Unit Cost</td><td className="font-mono text-gray-900">{'\u20AC'}{p.uc.toFixed(2)}</td></tr>
            {hasPP && (
              <>
                <tr><td className="text-gray-500 pr-2 py-0.5">Expected</td><td className="font-mono text-gray-900">{'\u20AC'}{sup.benchmark.toFixed(2)}{sup.eau > 0 ? <span className="text-gray-400 ml-1">(EAU {parseInt(sup.eau).toLocaleString()})</span> : ''}</td></tr>
                <tr><td className="text-gray-500 pr-2 py-0.5">Position</td><td className={`font-semibold ${pp > 1.5 ? 'text-red-600' : pp < 0.7 ? 'text-green-700' : 'text-gray-600'}`}>{Math.abs(Math.round((pp - 1) * 100))}% {pp > 1 ? 'above' : 'below'}</td></tr>
              </>
            )}
            {p.spend > 0 && <tr><td className="text-gray-500 pr-2 py-0.5">Spend</td><td className="font-mono text-gray-900">{'\u20AC'}{Math.round(p.spend).toLocaleString()}</td></tr>}
          </tbody>
        </table>
        {hasPP && (
          <div className={`text-[10px] font-medium mt-1 ${pp > 1.5 ? 'text-red-600' : pp < 0.7 ? 'text-green-700' : 'text-gray-600'}`}>
            {pp > 1.5 ? '\u26A0 Above expected \u2014 negotiation target' : pp < 0.7 ? '\u2713 Well below expected' : '\u25CB Within normal range'}
          </div>
        )}
        {!hasPP && !sup && <div className="text-[10px] text-gray-400 mt-1">No price position data available</div>}
        {/* Cohort mini-table */}
        {sup?.cohort_entries?.length > 0 && (
          <div className="mt-2 pt-1.5 border-t border-gray-100">
            <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              {humanizeCohortName(sup.cohort_name)} ({sup.cohort_size || sup.cohort_entries.length})
            </div>
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left font-medium pb-0.5 pr-2">Part</th>
                  <th className="text-left font-medium pb-0.5 pr-2">Supplier</th>
                  <th className="text-right font-medium pb-0.5 pr-2">UC</th>
                  <th className="text-right font-medium pb-0.5">EAU</th>
                </tr>
              </thead>
              <tbody>
                {sup.cohort_entries.sort((a, b) => b.unit_cost - a.unit_cost).slice(0, 6).map((e, i) => (
                  <tr key={i} className={e.supplier === p.supplier ? 'text-gray-900 font-medium' : 'text-gray-600'}>
                    <td className="pr-2 py-0.5 truncate max-w-[100px] font-mono">{e.pn?.slice(0, 12) || '\u2014'}</td>
                    <td className="pr-2 py-0.5 truncate max-w-[100px]">{e.supplier?.slice(0, 14)}</td>
                    <td className="text-right pr-2 py-0.5 font-mono whitespace-nowrap">{'\u20AC'}{e.unit_cost.toFixed(2)}</td>
                    <td className="text-right py-0.5 font-mono whitespace-nowrap">{e.eau > 0 ? Math.round(e.eau).toLocaleString() : '\u2014'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>,
      document.body
    )
  }

  return (
    <>
      <ChartFrame>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
          <text x={W - RIGHT} y={20} textAnchor="end" fontSize="12" fontWeight="700" fill="#059669">Potential savings: {formatRange(opp.estSavings)}</text>
          {/* Cohort label */}
          <text x={LEFT} y={20} fontSize="11" fontWeight="600" fill={TEXT_SECONDARY}>Cohort: {d.famLabel}</text>
          <text x={LEFT} y={35} fontSize="9" fill={TEXT_TERTIARY}>{d.partCount} parts{medianUC > 0 ? ` \u00B7 median \u20AC${medianUC.toFixed(2)}` : ''}{d.totalSpend > 0 ? ` \u00B7 \u20AC${Math.round(d.totalSpend).toLocaleString()} total spend` : ''}</text>
          {/* Header */}
          <text x={LEFT} y={TOP - 12} fontSize="10" fill={TEXT_TERTIARY}>Unit Cost</text>
          <text x={LEFT - 8} y={TOP - 12} textAnchor="end" fontSize="10" fill={TEXT_TERTIARY}>Part Number</text>
          {/* Median reference line */}
          {medianUC > 0 && (
            <g>
              <line x1={medianX} y1={TOP - 4} x2={medianX} y2={TOP + parts.length * ROW_H} stroke="#6366f1" strokeWidth="1.2" strokeDasharray="3 4" opacity="0.7" />
              <text x={medianX + 4} y={TOP + parts.length * ROW_H + 40} fontSize="9" fill="#6366f1">Median: {'\u20AC'}{medianUC.toFixed(2)}</text>
            </g>
          )}
          {/* Best-price reference line */}
          {bestUC > 0 && (
            <g>
              <line x1={bestX} y1={TOP - 4} x2={bestX} y2={TOP + parts.length * ROW_H} stroke="#10b981" strokeWidth="1.5" strokeDasharray="4 3" />
              <text x={bestX + 4} y={TOP + parts.length * ROW_H + 28} fontSize="9" fill="#059669">Best: {'\u20AC'}{bestUC.toFixed(2)}</text>
            </g>
          )}
          {/* Per-part bars */}
          {parts.map((p, i) => {
            const cy = TOP + i * ROW_H
            const barW = (p.uc / maxUC) * chartW
            const color = supColorMap[p.supplier] || '#94a3b8'
            const partLabel = p.desc ? `${p.pn} (${p.desc})` : (p.pn || '')
            return (
              <g key={i}>
                <text
                  x={LEFT - 8} y={cy + 16} textAnchor="end" fontSize="10"
                  fill={onPartClick ? '#1e5045' : TEXT_SECONDARY}
                  style={onPartClick ? { cursor: 'pointer' } : undefined}
                  className={onPartClick ? 'hover:underline' : ''}
                  onClick={() => handlePartClick(p.pn)}
                >{partLabel.slice(0, 30)}</text>
                <rect
                  x={LEFT} y={cy + 2} width={barW} height="20" rx="3" fill={color} opacity="0.8"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => handleBarEnter(e, i)}
                  onMouseLeave={handleBarLeave}
                />
                <text
                  x={LEFT + barW + 5} y={cy + 16} fontSize="10" fill={TEXT_TERTIARY}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => handleBarEnter(e, i)}
                  onMouseLeave={handleBarLeave}
                >{'\u20AC'}{p.uc.toFixed(2)} ({p.supplier?.slice(0, 14)})</text>
              </g>
            )
          })}
          {!expanded && allParts.length > 10 && <text x={LEFT} y={TOP + parts.length * ROW_H + 14} fontSize="9" fill={TEXT_TERTIARY}>+ {allParts.length - 10} more parts</text>}
          {/* Supplier legend */}
          {suppliers.slice(0, 6).map((s, i) => (
            <g key={i} transform={`translate(${LEFT + i * 120}, ${H - 20})`}>
              <rect width="10" height="10" rx="2" fill={supColorMap[s.name]} />
              <text x="14" y="9" fontSize="9" fill={TEXT_SECONDARY}>{s.name.slice(0, 12)}</text>
            </g>
          ))}
        </svg>
      </ChartFrame>
      {renderTooltip()}
      {allParts.length > 10 && (
        <button onClick={() => setExpanded(!expanded)} className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium mt-1 mb-1 cursor-pointer">
          {expanded ? '\u25B2 Show fewer' : `\u25BC Show all ${allParts.length} parts`}
        </button>
      )}
      <Insight>
        {d.hasPriceGap && d.hasConsolidation
          ? `Prices range \u20AC${d.best.uc.toFixed(2)}\u2013\u20AC${d.worst.uc.toFixed(2)} (${Math.round(d.spread)}\u00D7) across ${d.partCount} similar parts AND fragmented across ${d.supplierCount} suppliers. Both price-alignment and consolidation levers available.`
          : d.hasPriceGap
          ? `${d.partCount} engineering-similar parts ranging \u20AC${d.best.uc.toFixed(2)}\u2013\u20AC${d.worst.uc.toFixed(2)} (${Math.round(d.spread)}\u00D7). Best-in-family price serves as a negotiation target for parts above median.`
          : `Spend fragmented across ${d.supplierCount} suppliers for ${d.partCount} similar parts. Consolidate for volume leverage.`}
      </Insight>
    </>
  )
}

function QuickWinViz({ opp }) {
  const d = opp.vizData
  // Cap the visual scale so extreme outliers don't crush the alternative bar
  const rawMax = Math.max(d.current.pp, 2.0)
  const maxPP = Math.min(rawMax, d.current.pp * 1.15) // always show current bar at ~87% width
  const W = 700, H = 160
  const LEFT = 220, RIGHT = 120, TOP = 30, BOT = 25
  const chartW = W - LEFT - RIGHT
  const benchX = LEFT + (1.0 / maxPP) * chartW
  const Bar = ({ pp, y, color, label, capped }) => {
    const barW = Math.min((pp / maxPP) * chartW, chartW)
    return (
      <g>
        <text x={LEFT - 8} y={y + 16} textAnchor="end" fontSize="13" fill={TEXT_SECONDARY}>{label.slice(0, 22)}</text>
        <rect x={LEFT} y={y} width={barW} height="24" rx="4" fill={color} opacity="0.85" />
        <text x={LEFT + barW + 6} y={y + 17} fontSize="13" fontWeight="600" fill={color}>{pp.toFixed(2)}x</text>
      </g>
    )
  }
  const savePct = Math.round((1 - d.alternative.pp / d.current.pp) * 100)
  return (
    <>
      <ChartFrame>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
          <defs><marker id="oppArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 0 0 L 10 5 L 0 10 z" fill="#059669" /></marker></defs>
          <line x1={benchX} y1={TOP - 5} x2={benchX} y2={H - BOT + 5} stroke={TEXT_TERTIARY} strokeWidth="1.5" strokeDasharray="4,3" />
          <text x={benchX} y={TOP - 8} textAnchor="middle" fontSize="10" fill={TEXT_TERTIARY}>Benchmark</text>
          <Bar pp={d.current.pp} y={TOP + 8} color="#ef4444" label={d.current.name} />
          <Bar pp={d.alternative.pp} y={TOP + 65} color="#10b981" label={d.alternative.name} />
          {/* Savings callout below the bars */}
          <text x={LEFT + chartW / 2} y={H - BOT + 8} textAnchor="middle" fontSize="12" fontWeight="600" fill="#059669">
            Save {savePct}% ({formatRange(opp.estSavings)})
          </text>
          <line x1={LEFT + 10} y1={H - BOT - 4} x2={LEFT + chartW - 10} y2={H - BOT - 4} stroke="#059669" strokeWidth="1.5" markerEnd="url(#oppArrow)" opacity="0.6" />
        </svg>
      </ChartFrame>
      <Insight>A qualified alternative already exists at a lower price. No new qualification needed — just a sourcing decision.</Insight>
    </>
  )
}

function SiteGapViz({ opp }) {
  // Horizontal bars: unit cost per site, best site highlighted, gap to best shown.
  const d = opp.vizData
  const sites = d.sites
  const maxUC = Math.max(...sites.map(s => s.unit_cost))
  const best = d.best
  const ROW_H = 38
  const W = 760, H = Math.max(150, sites.length * ROW_H + 70)
  const LEFT = 150, RIGHT = 150, TOP = 50
  const chartW = W - LEFT - RIGHT
  const bestX = LEFT + (best / maxUC) * chartW
  return (
    <>
      <ChartFrame>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
          <text x={W - 10} y={20} textAnchor="end" fontSize="13" fontWeight="700" fill="#059669">Potential savings: {formatRange(opp.estSavings)}</text>
          {/* best-in-network reference line */}
          <line x1={bestX} y1={TOP - 6} x2={bestX} y2={H - 12} stroke="#059669" strokeWidth="1.5" strokeDasharray="4,3" />
          <text x={bestX} y={TOP - 12} textAnchor="middle" fontSize="10" fill="#059669">Best €{best.toFixed(2)}</text>
          {sites.map((s, i) => {
            const cy = TOP + i * ROW_H
            const barW = (s.unit_cost / maxUC) * chartW
            const isBest = s.unit_cost <= best
            const overPct = best > 0 ? Math.round((s.unit_cost / best - 1) * 100) : 0
            const color = isBest ? '#10b981' : overPct > 30 ? '#ef4444' : '#f59e0b'
            return (
              <g key={s.site + i}>
                <text x={LEFT - 8} y={cy + 17} textAnchor="end" fontSize="12" fill={TEXT_SECONDARY}>{s.site.slice(0, 16)}</text>
                <rect x={LEFT} y={cy + 4} width={barW} height="22" rx="3" fill={color} opacity="0.85" />
                <text x={LEFT + barW + 6} y={cy + 19} fontSize="11" fontWeight="600" fill={color}>
                  €{s.unit_cost.toFixed(2)} {isBest ? '· best' : `· +${overPct}%`}
                </text>
              </g>
            )
          })}
        </svg>
      </ChartFrame>
      <Insight>
        The <strong>same part</strong> ({d.pn}{d.desc ? ` — ${d.desc}` : ''}) costs €{best.toFixed(2)} at {d.bestSite} but up to €{Math.max(...sites.map(s => s.unit_cost)).toFixed(2)} elsewhere — a {Math.round((d.spread - 1) * 100)}% siloed-sourcing gap.
        Harmonizing every site to the best in-network price is clean savings with no requalification.
      </Insight>
    </>
  )
}

const VIZ = {
  negotiation: NegoViz,
  sole_source: SoleSourceViz,
  part_family: PartFamilyViz,
  quick_win: QuickWinViz,
  site_gap: SiteGapViz,
}

export default function OppViz({ opp, onPartClick }) {
  const Component = VIZ[opp.type]
  if (!Component || !opp.vizData) return null
  return <Component opp={opp} onPartClick={onPartClick} />
}
